import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Upload, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { patientsApi, requestsApi, Patient, PatientOfferStats } from '../../../services/api';
import PatientDetailModal from '../../../components/PatientDetailModal/PatientDetailModal';
import OfferPopover from '../../../components/OfferPopover/OfferPopover';
import { getRequirementStyle } from '../../../config/requirementConfig';
import { useAuth } from '../../../context/AuthContext';
import { LockedPatientInfo } from '../OfferCreate';
import { parsePatientIdsFromExcel, exportAoaToExcel } from '../../../utils/excelExport';
import './PatientSelectionStep.css';

type OfferType = 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY';
type TransportIntent = 'full' | 'ext_transport' | 'treatment_only' | 'provide_transport';

interface PatientSelectionStepProps {
  requestId: string;
  requestCategory?: string;
  shortlistedPatientIds: string[];
  selectedOfferType: { [patientId: string]: OfferType };
  transportIntent: { [patientId: string]: TransportIntent };
  lockedPatients?: Map<string, LockedPatientInfo>;
  onAddToShortlist: (patients: Patient[], offerType: OfferType) => void;
  onRemoveFromShortlist: (patientId: string) => void;
  onChangeOfferType: (patientId: string, offerType: OfferType) => void;
  onSetTransportIntent: (patientId: string, intent: TransportIntent) => void;
  onContinue: () => void;
  onHelp: () => void;
}

function PatientSelectionStep({
  requestId,
  requestCategory,
  shortlistedPatientIds,
  selectedOfferType,
  transportIntent,
  lockedPatients,
  onAddToShortlist,
  onRemoveFromShortlist,
  onChangeOfferType,
  onSetTransportIntent,
  onContinue,
  onHelp
}: PatientSelectionStepProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 500,
    total: 0,
    totalPages: 0
  });

  const [selectedPatientIds, setSelectedPatientIds] = useState<Set<string>>(new Set());
  const [offerStats, setOfferStats] = useState<Map<string, PatientOfferStats>>(new Map());
  const lastClickedIndex = useRef<number | null>(null);

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    dependency: '',
    transport: '',
    requirements: [] as string[],
    hasCompanions: '',
    offerStatus: '',
    myOfferType: ''
  });

  const [sortBy, setSortBy] = useState('patientId');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [requirementsDropdownOpen, setRequirementsDropdownOpen] = useState(false);
  const [hideLockedPatients, setHideLockedPatients] = useState(false);

  const [showPatientDetail, setShowPatientDetail] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; patientIds: string[] } | null>(null);

  // Excel import
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importFeedback, setImportFeedback] = useState<{ matched: number; notFound: number; notFoundIds: string[] } | null>(null);

  useEffect(() => {
    loadPatients();
  }, [pagination.page, pagination.limit, sortBy, sortOrder, filters]);

  useEffect(() => {
    loadOfferStatistics();
  }, [requestId]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  // Close requirements dropdown on click outside
  const requirementsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!requirementsDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (requirementsRef.current && !requirementsRef.current.contains(e.target as Node)) {
        setRequirementsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [requirementsDropdownOpen]);

  const loadOfferStatistics = async () => {
    try {
      const response = await requestsApi.getPatientOffers(requestId);
      const statsMap = new Map<string, PatientOfferStats>();
      response.data.forEach(stat => {
        statsMap.set(stat.patientId, stat);
      });
      setOfferStats(statsMap);
    } catch (error) {
      console.error('Failed to load offer statistics:', error);
    }
  };

  const loadPatients = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder
      };

      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.dependency) params.dependency = filters.dependency;
      if (filters.transport) params.transport = filters.transport;
      if (filters.requirements.length > 0) params.requirements = filters.requirements;
      if (filters.hasCompanions) params.hasCompanions = filters.hasCompanions === 'true';

      const response = await patientsApi.getByRequestId(requestId, params);
      setPatients(response.data.patients);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Failed to load patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRequirementToggle = (req: string) => {
    setFilters(prev => ({
      ...prev,
      requirements: prev.requirements.includes(req)
        ? prev.requirements.filter(r => r !== req)
        : [...prev.requirements, req]
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      dependency: '',
      transport: '',
      requirements: [],
      hasCompanions: '',
      offerStatus: '',
      myOfferType: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const requirements = useMemo(() => {
    const uniqueRequirements = new Set<string>();
    patients.forEach(patient => {
      if (patient.medicalRequirements && Array.isArray(patient.medicalRequirements)) {
        patient.medicalRequirements.forEach((req: any) => {
          if (req.requirementType) {
            uniqueRequirements.add(req.requirementType);
          }
        });
      }
    });
    return Array.from(uniqueRequirements).sort();
  }, [patients]);

  // Client-side filters: offer status + my offer type (must be above handleRowClick)
  const hasLockedPatients = lockedPatients && lockedPatients.size > 0;

  const filteredPatients = useMemo(() => {
    let result = patients;

    // Hide locked patients (already in other offers)
    if (hideLockedPatients && lockedPatients) {
      result = result.filter(p => !lockedPatients.has(p.id));
    }

    // Offer status filter (existing offers from other nations)
    if (filters.offerStatus) {
      result = result.filter(p => {
        const stats = offerStats.get(p.id);
        const hasTreatment = stats ? (stats.offersByType.TREATMENT > 0 || (stats.offersByType.TREATMENT_ONLY || 0) > 0) : false;
        const hasTransport = stats ? stats.offersByType.TRANSPORT > 0 : false;
        const hasBoth = stats ? (stats.offersByType.BOTH > 0 || (stats.offersByType.TREATMENT_AND_TRANSPORT || 0) > 0) : false;
        // Check for ext. transport partner via transportNation in offersByNation
        const hasExtTransport = stats?.offersByNation?.some(
          o => o.transportNation && o.transportNation !== o.nation
        ) || false;

        switch (filters.offerStatus) {
          case 'FULL_OFFER': return hasBoth;
          case 'COMBINED': return !hasBoth && hasTreatment && (hasTransport || hasExtTransport);
          case 'TREATMENT_ONLY': return hasTreatment && !hasBoth && !hasTransport && !hasExtTransport;
          case 'NONE': return !hasTreatment && !hasTransport && !hasBoth;
          default: return true;
        }
      });
    }

    // My offer type filter
    if (filters.myOfferType) {
      result = result.filter(p => {
        const isInOffer = shortlistedPatientIds.includes(p.id);
        switch (filters.myOfferType) {
          case 'IN_OFFER': return isInOffer;
          case 'NOT_IN_OFFER': return !isInOffer;
          case 'TREATMENT_AND_TRANSPORT': return isInOffer && selectedOfferType[p.id] === 'TREATMENT_AND_TRANSPORT';
          case 'TREATMENT_ONLY': return isInOffer && selectedOfferType[p.id] === 'TREATMENT_ONLY';
          default: return true;
        }
      });
    }

    return result;
  }, [patients, filters.offerStatus, filters.myOfferType, offerStats, shortlistedPatientIds, selectedOfferType, hideLockedPatients, lockedPatients]);

  // Row click with Ctrl/Shift support
  const handleRowClick = useCallback((e: React.MouseEvent, patient: Patient, rowIndex: number) => {
    if ((e.target as HTMLElement).closest('.patient-id-link')) return;
    if ((e.target as HTMLElement).tagName === 'INPUT') return;

    if (e.ctrlKey || e.metaKey) {
      setSelectedPatientIds(prev => {
        const next = new Set(prev);
        if (next.has(patient.id)) {
          next.delete(patient.id);
        } else {
          next.add(patient.id);
        }
        return next;
      });
      lastClickedIndex.current = rowIndex;
    } else if (e.shiftKey && lastClickedIndex.current !== null) {
      e.preventDefault();
      const start = Math.min(lastClickedIndex.current, rowIndex);
      const end = Math.max(lastClickedIndex.current, rowIndex);
      const rangeIds = filteredPatients.slice(start, end + 1)
        .filter(p => !lockedPatients?.has(p.id))
        .map(p => p.id);
      setSelectedPatientIds(prev => {
        const next = new Set(prev);
        rangeIds.forEach(id => next.add(id));
        return next;
      });
    } else {
      setSelectedPatientIds(new Set([patient.id]));
      lastClickedIndex.current = rowIndex;
    }
  }, [filteredPatients]);

  // Context menu on right-click
  const handleContextMenu = useCallback((e: React.MouseEvent, patient: Patient) => {
    e.preventDefault();
    let targetIds: string[];
    if (selectedPatientIds.has(patient.id) && selectedPatientIds.size > 1) {
      targetIds = Array.from(selectedPatientIds);
    } else {
      targetIds = [patient.id];
    }
    setContextMenu({ x: e.clientX, y: e.clientY, patientIds: targetIds });
  }, [selectedPatientIds]);

  const handleContextAction = (action: 'full' | 'ext_transport' | 'treatment_only' | 'provide_transport' | 'remove') => {
    if (!contextMenu) return;
    const targetPatients = filteredPatients.filter(p => contextMenu.patientIds.includes(p.id) && !lockedPatients?.has(p.id));

    if (action === 'remove') {
      contextMenu.patientIds.forEach(id => {
        if (shortlistedPatientIds.includes(id)) {
          onRemoveFromShortlist(id);
        }
      });
    } else {
      // Map context menu action to offer type + transport intent
      let offerType: OfferType;
      let intent: TransportIntent;
      if (action === 'full') {
        offerType = 'TREATMENT_AND_TRANSPORT';
        intent = 'full';
      } else if (action === 'ext_transport') {
        offerType = 'TREATMENT_ONLY';
        intent = 'ext_transport';
      } else if (action === 'provide_transport') {
        offerType = 'TREATMENT_ONLY';
        intent = 'provide_transport';
      } else {
        offerType = 'TREATMENT_ONLY';
        intent = 'treatment_only';
      }

      const toAdd = targetPatients.filter(p => !shortlistedPatientIds.includes(p.id));
      const toChange = contextMenu.patientIds.filter(id => shortlistedPatientIds.includes(id));

      if (toAdd.length > 0) {
        onAddToShortlist(toAdd, offerType);
      }
      // Set offer type + transport intent for all targets
      contextMenu.patientIds.forEach(id => {
        if (toChange.includes(id)) {
          onChangeOfferType(id, offerType);
        }
        onSetTransportIntent(id, intent);
      });
    }
    setContextMenu(null);
    setSelectedPatientIds(new Set());
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) return '\u21C5';
    return sortOrder === 'asc' ? '\u2191' : '\u2193';
  };

  const getOfferIcons = (patientId: string) => {
    const stats = offerStats.get(patientId);
    const hasTreatment = stats ? stats.offersByType.TREATMENT > 0 : false;
    const hasTransport = stats ? stats.offersByType.TRANSPORT > 0 : false;
    const hasBoth = stats ? stats.offersByType.BOTH > 0 : false;
    // Check if any offer entry has an external transport nation (via transportProviderNationId)
    const hasExtTransportNation = stats?.offersByNation?.some(
      o => o.transportNation && o.transportNation !== o.nation
    ) || false;

    // Priority: 1) T&T combined, 2) Treatment + external Transport, 3) Treatment only, 4) No offers
    if (hasBoth) {
      return (
        <span className="offer-icons">
          <img src="/icons/offer_combined_gruen.webp" alt="Treatment &amp; Transport" width={42} height={24} />
        </span>
      );
    }
    if (hasTreatment && (hasTransport || hasExtTransportNation)) {
      return (
        <span className="offer-icons">
          <img src="/icons/delivery_gelb.webp" alt="Ext. Transport" width={24} height={24} />
          <img src="/icons/hospital_gruen.webp" alt="Treatment" width={24} height={24} />
        </span>
      );
    }
    if (hasTreatment) {
      return (
        <span className="offer-icons">
          <img src="/icons/delivery_leer.webp" alt="No Transport" width={24} height={24} />
          <img src="/icons/hospital_gruen.webp" alt="Treatment" width={24} height={24} />
        </span>
      );
    }
    return (
      <span className="offer-icons">
        <img src="/icons/delivery_leer.webp" alt="No Transport" width={24} height={24} />
        <img src="/icons/hospital_leer.webp" alt="No Treatment" width={24} height={24} />
      </span>
    );
  };

  const getMyOfferBadge = (patientId: string) => {
    if (!shortlistedPatientIds.includes(patientId)) return null;
    const type = selectedOfferType[patientId];
    const intent = transportIntent[patientId];
    if (type === 'TREATMENT_AND_TRANSPORT') {
      return (
        <span className="offer-icons">
          <img src="/icons/offer_combined_gruen.webp" alt="Full Offer" width={42} height={24} />
        </span>
      );
    }
    // Treatment Only — show ext_transport vs provide_transport vs plain treatment
    if (intent === 'ext_transport') {
      return (
        <span className="offer-icons">
          <img src="/icons/delivery_gelb.webp" alt="Ext. Transport" width={24} height={24} />
          <img src="/icons/hospital_gruen.webp" alt="Treatment" width={24} height={24} />
        </span>
      );
    }
    if (intent === 'provide_transport') {
      return (
        <span className="offer-icons">
          <img src="/icons/delivery_gruen.webp" alt="Provide Transport" width={24} height={24} />
          <img src="/icons/hospital_gelb.webp" alt="Other Nation Treatment" width={24} height={24} />
        </span>
      );
    }
    return (
      <span className="offer-icons">
        <img src="/icons/delivery_leer.webp" alt="No Transport" width={24} height={24} />
        <img src="/icons/hospital_gruen.webp" alt="Treatment Only" width={24} height={24} />
      </span>
    );
  };

  // === Excel Import: read Patient IDs from file and auto-select matching patients ===
  const handleImportExcel = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const buffer = evt.target?.result as ArrayBuffer;
        const { patientIds } = parsePatientIdsFromExcel(buffer);

        if (patientIds.length === 0) {
          setImportFeedback({ matched: 0, notFound: 0, notFoundIds: [] });
          return;
        }

        // Match imported IDs against loaded patients (by patientId field)
        const patientIdSet = new Set(patientIds.map(id => id.toUpperCase()));
        const matchedPatients: Patient[] = [];
        const matchedIds = new Set<string>();

        for (const patient of patients) {
          const pid = (patient.patientId || '').toUpperCase();
          if (patientIdSet.has(pid) && !lockedPatients?.has(patient.id)) {
            matchedPatients.push(patient);
            matchedIds.add(pid);
          }
        }

        // Find IDs that weren't matched
        const notFoundIds = patientIds.filter(id => !matchedIds.has(id.toUpperCase()));

        // Auto-add matched patients to shortlist as TREATMENT_AND_TRANSPORT
        if (matchedPatients.length > 0) {
          onAddToShortlist(matchedPatients, 'TREATMENT_AND_TRANSPORT');
        }

        setImportFeedback({
          matched: matchedPatients.length,
          notFound: notFoundIds.length,
          notFoundIds: notFoundIds.slice(0, 10),
        });
      } catch {
        setImportFeedback({ matched: 0, notFound: 0, notFoundIds: [] });
      }
    };
    reader.readAsArrayBuffer(file);

    // Reset file input so same file can be re-imported
    e.target.value = '';
  }, [patients, lockedPatients, onAddToShortlist]);

  // === Excel Export: export current patient list for re-import ===
  const handleExportForImport = useCallback(() => {
    if (patients.length === 0) return;
    const headers = [
      'Patient ID', 'Initials', 'Age', 'Gender', 'Civil / Military Status',
      'Nation', 'Nationality', 'Condition ID', 'Primary Diagnosis', 'Secondary Condition',
      'Medical Priority', 'Transport Mode (Lying/Seated)', 'Dependency Level (Mobility)',
      'Medical Requirements',
      'Companions Count', 'Companions Details',
      'UCPM Exit Point', 'Destination Country',
      'Clinical History', 'Notes',
    ];
    const rows = patients
      .filter(p => !lockedPatients?.has(p.id))
      .map(p => [
        p.patientId || '',
        p.patientInitials || '',
        p.patientAge ?? '',
        p.patientGender || '',
        p.patientStatus || '',
        p.sendingNation || '',
        p.nationality || '',
        p.medicalPrimaryConditionId || '',
        p.medicalPrimaryConditionDiagnosis || '',
        p.medicalSecondaryConditionText || '',
        p.medicalPriority || '',
        p.medicalTransport || '',
        p.medicalDependency || '',
        Array.isArray(p.medicalRequirements) ? p.medicalRequirements.map((r: { requirementType: string }) => r.requirementType).join(', ') : '',
        p.patientCompanionsCount ?? 0,
        p.patientCompanionsText || '',
        p.transportExitPoint || '',
        p.transportDestinationCountry || '',
        p.narrativeClinical || '',
        p.narrativeAdditionalRemarks || '',
      ]);
    exportAoaToExcel(
      headers, rows,
      `ARCOS_Import_Template_${requestId}`,
      'Patients',
      {
        title: `Patient Selection Template — ${requestId}`,
        subtitle: `${rows.length} patient(s) | Delete rows you do NOT want, keep only selected patients, then import this file back in Create Offer`,
      }
    );
  }, [patients, lockedPatients, requestId]);

  const activeFilterCount = [
    filters.status, filters.priority, filters.dependency,
    filters.offerStatus, filters.search, filters.hasCompanions,
    filters.myOfferType, filters.transport
  ].filter(Boolean).length + (filters.requirements.length > 0 ? 1 : 0);

  // Context menu: check if any target is in offer
  const contextHasInOffer = contextMenu ? contextMenu.patientIds.some(id => shortlistedPatientIds.includes(id)) : false;

  // Context menu: check if "Provide Transport" should be shown
  // Visible when at least one OTHER nation has a treatment-only offer (no full offer exists)
  const contextCanProvideTransport = useMemo(() => {
    if (!contextMenu) return false;
    const userNation = user?.nation || '';
    return contextMenu.patientIds.every(id => {
      const stats = offerStats.get(id);
      if (!stats || !stats.offersByNation) return false;
      const hasFullOffer = stats.offersByType.BOTH > 0 || (stats.offersByType.TREATMENT_AND_TRANSPORT || 0) > 0;
      if (hasFullOffer) return false;
      const treatmentNations = new Set(
        stats.offersByNation
          .filter(o => (o.offerType === 'TREATMENT_ONLY' || o.offerType === 'TREATMENT') && !o.transportNation)
          .map(o => o.nation)
      );
      // At least one foreign nation must have treatment
      return treatmentNations.size > 0 &&
        !(treatmentNations.size === 1 && treatmentNations.has(userNation));
    });
  }, [contextMenu, offerStats, user]);

  return (
    <div className="patient-selection-step">
      {/* === STICKY TOOLBAR === */}
      <div className="sticky-toolbar">
        {/* Row 1: Step title + Filters */}
        <div className="toolbar-row toolbar-filters">
          <span className="step-label">{t('requests.step', { number: 1 })}: {t('offers.selectPatients')}</span>

          <input
            type="text"
            placeholder={`${t('common.search')} ID / ${t('patients.diagnosis')}...`}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="filter-input"
            style={{ width: '180px' }}
          />

          <select
            value={filters.myOfferType}
            onChange={(e) => handleFilterChange('myOfferType', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('offers.title')}</option>
            <option value="IN_OFFER">{t('offers.shortlist')}</option>
            <option value="NOT_IN_OFFER">{t('common.none')}</option>
            <option value="TREATMENT_AND_TRANSPORT">T&amp;T</option>
            <option value="TREATMENT_ONLY">{t('offers.treatment')}</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.status')}</option>
            <option value="CIVIL">Civil</option>
            <option value="MILITARY">Military</option>
            <option value="TBA">TBA</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.priority')}</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
            <option value="TBA">TBA</option>
          </select>

          <select
            value={filters.dependency}
            onChange={(e) => handleFilterChange('dependency', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('patients.dependency')}</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="TBA">TBA</option>
          </select>

          <div className="requirements-dropdown" ref={requirementsRef}>
            <button
              className="dropdown-toggle"
              onClick={() => setRequirementsDropdownOpen(!requirementsDropdownOpen)}
            >
              Req. {filters.requirements.length > 0 && `(${filters.requirements.length})`}
            </button>
            {requirementsDropdownOpen && (
              <div className="dropdown-menu">
                {requirements.length === 0 ? (
                  <div className="dropdown-item-empty">{t('common.noData')}</div>
                ) : (
                  requirements.map(req => {
                    const style = getRequirementStyle(req);
                    return (
                      <label key={req} className="dropdown-item">
                        <input
                          type="checkbox"
                          checked={filters.requirements.includes(req)}
                          onChange={() => handleRequirementToggle(req)}
                        />
                        <span
                          className="badge-requirement"
                          style={{ background: style.bg, color: style.color, padding: '2px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: 700 }}
                        >
                          {style.label}
                        </span>
                        <span>{req.replace(/_/g, ' ')}</span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <select
            value={filters.transport}
            onChange={(e) => handleFilterChange('transport', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('offers.transport')}</option>
            <option value="LYING">{t('patients.lying')}</option>
            <option value="SEATED">{t('patients.seated')}</option>
            <option value="TBA">TBA</option>
          </select>

          <select
            value={filters.hasCompanions}
            onChange={(e) => handleFilterChange('hasCompanions', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('requests.accompaniment')}</option>
            <option value="true">{t('common.yes')}</option>
            <option value="false">{t('common.no')}</option>
          </select>

          <select
            value={filters.offerStatus}
            onChange={(e) => handleFilterChange('offerStatus', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('offers.title')}</option>
            <option value="FULL_OFFER">{t('offers.fullOffer')}</option>
            <option value="COMBINED">{t('offers.combinedOffer')}</option>
            <option value="TREATMENT_ONLY">{t('offers.treatmentOffer')}</option>
            <option value="NONE">{t('common.none')}</option>
          </select>

          {hasLockedPatients && (
            <label className="filter-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hideLockedPatients}
                onChange={(e) => setHideLockedPatients(e.target.checked)}
              />
              {t('offers.hideLockedPatients', 'Hide patients already selected in other offers')}
            </label>
          )}

          {activeFilterCount > 0 && (
            <button onClick={handleResetFilters} className="btn-reset" title={t('common.reset')}>
              {t('common.reset')} ({activeFilterCount})
            </button>
          )}
        </div>

        {/* Row 2: Actions + Pagination + Step Navigation */}
        <div className="toolbar-row toolbar-actions">
          <span className="selection-info">
            {selectedPatientIds.size > 0 ? (
              <>
                <strong>{selectedPatientIds.size}</strong> {t('common.selected', { count: selectedPatientIds.size })}
                <button className="btn-link" onClick={() => setSelectedPatientIds(new Set())}>
                  {t('common.clear')}
                </button>
              </>
            ) : (
              <>{pagination.total} {t('patients.title').toLowerCase()}</>
            )}
          </span>

          {selectedPatientIds.size === 0 && (
            <span className="multi-select-hint">
              Ctrl+Click to multi-select, Shift+Click for range, Right-Click to set offer type
            </span>
          )}

          <span className="toolbar-separator" />

          {/* Pagination */}
          <div className="toolbar-pagination">
            <button
              className="btn-page"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              title={t('components.dataTable.previousPage')}
            >
              &lt;
            </button>
            <span className="page-info">
              {pagination.page}/{pagination.totalPages}
            </span>
            <button
              className="btn-page"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.totalPages}
              title={t('components.dataTable.nextPage')}
            >
              &gt;
            </button>
            <select
              className="page-size"
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </div>

          <span className="toolbar-separator" />

          <span className="offer-count-info">
            {t('offers.shortlist')}: <strong>{shortlistedPatientIds.length}</strong>
          </span>

          <span className="toolbar-separator" />

          {/* Excel Import/Export for batch patient selection */}
          <button
            className="btn btn-sm btn-outline"
            onClick={handleExportForImport}
            title="Export patient list as Excel template — edit it and re-import to select patients"
          >
            <FileSpreadsheet size={14} style={{ verticalAlign: 'middle', marginRight: '3px' }} />
            Export
          </button>
          <button
            className="btn btn-sm btn-outline"
            onClick={() => fileInputRef.current?.click()}
            title="Import Excel file to auto-select patients by Patient ID"
          >
            <Upload size={14} style={{ verticalAlign: 'middle', marginRight: '3px' }} />
            Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportExcel}
          />

          <div className="toolbar-spacer" />

          <button
            className="btn btn-primary btn-continue"
            onClick={onContinue}
            disabled={shortlistedPatientIds.length === 0}
          >
            {t('common.next')} ({shortlistedPatientIds.length}) <ArrowRight size={14} />
          </button>
        </div>

        {/* Import feedback banner */}
        {importFeedback && (
          <div className={`import-feedback ${importFeedback.matched > 0 ? 'import-feedback-success' : 'import-feedback-warning'}`}>
            <span>
              {importFeedback.matched > 0 ? (
                <><strong>{importFeedback.matched}</strong> patient(s) imported and added to shortlist.</>
              ) : (
                <>No matching patients found in the imported file.</>
              )}
              {importFeedback.notFound > 0 && (
                <> <strong>{importFeedback.notFound}</strong> ID(s) not found{importFeedback.notFoundIds.length > 0 && (
                  <>: {importFeedback.notFoundIds.join(', ')}{importFeedback.notFound > 10 && '...'}</>
                )}.</>
              )}
            </span>
            <button className="btn-link" onClick={() => setImportFeedback(null)} style={{ marginLeft: '8px' }}>
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* === SCROLLABLE TABLE === */}
      <div className="table-scroll-area">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <table className="patient-table">
            <thead>
              <tr>
                <th style={{ width: '32px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={`${t('common.selectAll')} / ${t('common.deselectAll')}`}
                    checked={filteredPatients.length > 0 && filteredPatients.filter(p => !lockedPatients?.has(p.id)).every(p => selectedPatientIds.has(p.id))}
                    ref={(el) => {
                      if (el) {
                        const selectablePatients = filteredPatients.filter(p => !lockedPatients?.has(p.id));
                        const selectedCount = selectablePatients.filter(p => selectedPatientIds.has(p.id)).length;
                        el.indeterminate = selectedCount > 0 && selectedCount < selectablePatients.length;
                      }
                    }}
                    onChange={() => {
                      const selectablePatients = filteredPatients.filter(p => !lockedPatients?.has(p.id));
                      const allSelected = selectablePatients.every(p => selectedPatientIds.has(p.id));
                      if (allSelected) {
                        setSelectedPatientIds(new Set());
                      } else {
                        setSelectedPatientIds(new Set(selectablePatients.map(p => p.id)));
                      }
                    }}
                  />
                </th>
                <th style={{ width: '56px', cursor: 'pointer' }} onClick={() => handleSort('myOffer')} title={t('offers.title')}>
                  {t('offers.title')} {sortBy === 'myOffer' ? (sortOrder === 'asc' ? '\u2191' : '\u2193') : ''}
                </th>
                <th onClick={() => handleSort('patientId')} style={{ cursor: 'pointer' }}>
                  {t('patients.patientId')} {renderSortIcon('patientId')}
                </th>
                <th>{t('common.nation')}</th>
                <th style={{ width: '32px' }}></th>
                <th onClick={() => handleSort('gender')} style={{ cursor: 'pointer' }}>
                  {t('patients.gender')} {renderSortIcon('gender')}
                </th>
                <th onClick={() => handleSort('age')} style={{ cursor: 'pointer' }}>
                  {t('patients.age')} {renderSortIcon('age')}
                </th>
                <th>{t('patients.condition')}</th>
                <th onClick={() => handleSort('priority')} style={{ cursor: 'pointer' }}>
                  {t('common.priority')} {renderSortIcon('priority')}
                </th>
                <th>{t('patients.dependency')}</th>
                <th>{t('patients.treatmentNeeded')}</th>
                <th style={{ width: '40px' }}></th>
                <th style={{ width: '36px' }}></th>
                <th>{t('offers.title')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map((patient, index) => {
                const lockedInfo = lockedPatients?.get(patient.id);
                const isLocked = !!lockedInfo;
                const isInShortlist = shortlistedPatientIds.includes(patient.id);
                const isSelected = selectedPatientIds.has(patient.id);
                let rowClass = '';
                if (isLocked) rowClass = 'row-locked';
                else if (isInShortlist) rowClass = 'in-shortlist';
                if (isSelected && !isLocked) rowClass += ' row-selected';
                return (
                  <tr
                    key={patient.id}
                    className={rowClass.trim()}
                    onClick={(e) => !isLocked && handleRowClick(e, patient, index)}
                    onContextMenu={(e) => { if (isLocked) { e.preventDefault(); return; } handleContextMenu(e, patient); }}
                    title={isLocked ? `${t('offers.title')} #${lockedInfo.offerIndex}` : undefined}
                  >
                    <td style={{ textAlign: 'center' }}>
                      {!isLocked && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedPatientIds(prev => {
                              const next = new Set(prev);
                              if (next.has(patient.id)) {
                                next.delete(patient.id);
                              } else {
                                next.add(patient.id);
                              }
                              return next;
                            });
                          }}
                        />
                      )}
                    </td>
                    <td className="my-offer-cell">
                      {isLocked ? (
                        <span className="locked-badge" title={`In Offer #${lockedInfo.offerIndex}`}>
                          <span className="locked-icon">&#128274;</span>
                          <span className="locked-label">#{lockedInfo.offerIndex}</span>
                        </span>
                      ) : getMyOfferBadge(patient.id)}
                    </td>
                    <td>
                      <span
                        className="patient-id patient-id-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPatient(patient);
                          setShowPatientDetail(true);
                        }}
                      >
                        {patient.patientId}
                      </span>
                    </td>
                    <td>{patient.nationality || 'N/A'}</td>
                    <td>
                      <span className={`status-circle status-${patient.patientStatus.toLowerCase()}`}>
                        {patient.patientStatus === 'CIVIL' ? 'C' : patient.patientStatus === 'MILITARY' ? 'M' : '?'}
                      </span>
                    </td>
                    <td>{patient.patientGender || 'N/A'}</td>
                    <td>{patient.patientAge || 'N/A'}</td>
                    <td className="medical-condition">{patient.medicalPrimaryConditionDiagnosis || 'N/A'}</td>
                    <td>
                      <span className={`triage-badge triage-${(patient.medicalPriority || 'unknown').toLowerCase()}`}>
                        {patient.medicalPriority || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {patient.medicalDependency && (
                        <span className={`dependency-badge dependency-${patient.medicalDependency.toLowerCase()}`}>
                          {patient.medicalDependency}
                        </span>
                      )}
                    </td>
                    <td>
                      {patient.medicalRequirements && patient.medicalRequirements.length > 0 ? (
                        <div className="requirements-badges">
                          {patient.medicalRequirements.map((req: any) => {
                            const style = getRequirementStyle(req.requirementType);
                            return (
                              <span
                                key={req.id}
                                className="badge-requirement"
                                style={{ background: style.bg, color: style.color }}
                                title={req.requirementType.replace(/_/g, ' ')}
                              >
                                {style.label}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {patient.medicalTransport === 'LYING' && (
                        <img src="/icons/patient-liegend.webp" alt="Lying" width={20} height={20} title="Lying" />
                      )}
                      {patient.medicalTransport === 'SEATED' && (
                        <img src="/icons/patient-sitzend.webp" alt="Seated" width={20} height={20} title="Seated" />
                      )}
                      {patient.medicalTransport === 'TBA' && (
                        <span>&#10067; TBA</span>
                      )}
                    </td>
                    <td>
                      {patient.patientCompanionsCount > 0 && (
                        <span className="companions-indicator">
                          <img src="/icons/benutzer.webp" alt="Companions" width={16} height={16} />
                          <span>{patient.patientCompanionsCount}</span>
                        </span>
                      )}
                    </td>
                    <td>
                      <OfferPopover offers={offerStats.get(patient.id)?.offersByNation || []}>
                        {getOfferIcons(patient.id)}
                      </OfferPopover>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header">
            {contextMenu.patientIds.length} {t('patients.title').toLowerCase()}
          </div>
          {requestCategory === 'TRANSPORT' ? (
            /* Transport-only request: only transport option available */
            <button className="context-menu-item" onClick={() => handleContextAction('provide_transport')}>
              <span>{t('offers.transport')}</span>
              <span className="context-menu-icons">
                <img src="/icons/delivery_gruen.webp" alt="" width={24} height={24} />
              </span>
            </button>
          ) : (
            <>
              <button className="context-menu-item" onClick={() => handleContextAction('full')}>
                <span>{t('offers.fullOffer')}</span>
                <span className="context-menu-icons">
                  <img src="/icons/offer_combined_gruen.webp" alt="" width={42} height={24} />
                </span>
              </button>
              <button className="context-menu-item" onClick={() => handleContextAction('ext_transport')}>
                <span>{t('offers.treatment')} + Ext. {t('offers.transport')}</span>
                <span className="context-menu-icons">
                  <img src="/icons/delivery_gelb.webp" alt="" width={24} height={24} />
                  <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                </span>
              </button>
              <button className="context-menu-item" onClick={() => handleContextAction('treatment_only')}>
                <span>{t('offers.treatmentOffer')}</span>
                <span className="context-menu-icons">
                  <img src="/icons/delivery_leer.webp" alt="" width={24} height={24} />
                  <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                </span>
              </button>
              {contextCanProvideTransport && (
                <button className="context-menu-item" onClick={() => handleContextAction('provide_transport')}>
                  <span>{t('offers.transport')}</span>
                  <span className="context-menu-icons">
                    <img src="/icons/delivery_gruen.webp" alt="" width={24} height={24} />
                    <img src="/icons/hospital_gelb.webp" alt="" width={24} height={24} />
                  </span>
                </button>
              )}
            </>
          )}
          {contextHasInOffer && (
            <>
              <div className="context-menu-divider" />
              <button className="context-menu-item context-menu-remove" onClick={() => handleContextAction('remove')}>
                <span>{t('offers.removeFromShortlist')}</span>
                <span className="context-menu-icons">
                  <img src="/icons/cancel.webp" alt="" width={24} height={24} />
                </span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Patient Detail Modal */}
      {showPatientDetail && selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          onClose={() => {
            setShowPatientDetail(false);
            setSelectedPatient(null);
          }}
        />
      )}

    </div>
  );
}

export default PatientSelectionStep;
